import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  getCatalogOrder,
  setCatalogOrderStatus,
  addCatalogOrderNote,
  CATALOG_ORDER_STATUSES,
  type CatalogOrderDoc,
  type CatalogOrderStatus,
} from '../../lib/store';

interface OrderDetailDrawerProps {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

export function OrderDetailDrawer({
  open,
  orderId,
  onClose,
  onUpdated,
}: OrderDetailDrawerProps) {
  const { t } = useI18n();
  const [order, setOrder] = useState<CatalogOrderDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status transition state
  const [selectedStatus, setSelectedStatus] = useState<CatalogOrderStatus>('pending');
  const [statusNote, setStatusNote] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // New staff note state
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const fetchOrder = async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCatalogOrder(orderId);
      setOrder(data);
      setSelectedStatus(data.status);
    } catch (err: any) {
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && orderId) {
      fetchOrder();
      setStatusNote('');
      setNotifyCustomer(false);
      setNewNote('');
    }
  }, [open, orderId]);

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId) return;

    setUpdatingStatus(true);
    setError(null);
    try {
      await setCatalogOrderStatus({
        id: orderId,
        status: selectedStatus,
        note: statusNote.trim() || undefined,
        notifyCustomer,
      });
      setStatusNote('');
      await fetchOrder();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId || !newNote.trim()) return;

    setAddingNote(true);
    setError(null);
    try {
      await addCatalogOrderNote(orderId, newNote.trim());
      setNewNote('');
      await fetchOrder();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusBadgeVariant = (status: CatalogOrderStatus): 'success' | 'warning' | 'info' | 'slate' | 'danger' => {
    switch (status) {
      case 'delivered':
      case 'completed':
      case 'approved':
        return 'success';
      case 'pending':
      case 'submitted':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'rejected':
      case 'cancelled':
      case 'refunded':
        return 'danger';
      default:
        return 'slate';
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={order ? `${t('store.orders.order')} #${order.orderNumber}` : t('store.orders.details')}
      size="lg"
    >
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : order ? (
        <div className="space-y-6">
          {error && (
            <div className="rounded-xl bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
              {error}
            </div>
          )}

          {/* Header Snapshot */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface-subtle p-4 dark:border-slate-800 dark:bg-slate-850">
            <div>
              <span className="text-xs text-ink-3 dark:text-slate-400">{t('store.orders.orderNumber')}</span>
              <h3 className="text-lg font-bold text-ink dark:text-white">#{order.orderNumber}</h3>
              <span className="text-[11px] text-ink-3 dark:text-slate-400">
                {new Date(order.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={getStatusBadgeVariant(order.status)}>
                {t(`store.status.${order.status}`)}
              </Badge>
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300 uppercase">
                {order.source}
              </span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <h4 className="mb-3 text-xs font-bold text-ink-2 dark:text-slate-300">
              {t('store.orders.customerInfo')}
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-xs">
              <div>
                <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.customerName')}</span>
                <span className="font-semibold text-ink dark:text-white">{order.customerName}</span>
              </div>
              <div>
                <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.customerPhone')}</span>
                <span className="font-semibold font-mono text-brand dark:text-brand-tint">
                  {order.customerPhone}
                </span>
              </div>
              <div className="sm:col-span-2">
                <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.shippingAddress')}</span>
                <span className="text-ink dark:text-white">{order.shippingAddress}</span>
              </div>
              {order.deliveryDate && (
                <div>
                  <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.deliveryDate')}</span>
                  <span className="text-ink dark:text-white">{order.deliveryDate}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <h4 className="mb-3 text-xs font-bold text-ink-2 dark:text-slate-300">
              {t('store.orders.items')} ({order.items.length})
            </h4>
            <div className="divide-y divide-line dark:divide-slate-800">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2.5 text-xs">
                  <div className="flex items-center gap-3">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="h-10 w-10 rounded-lg object-cover border border-line dark:border-slate-700"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-subtle text-ink-3 dark:bg-slate-800">
                        📦
                      </div>
                    )}
                    <div>
                      <span className="font-semibold text-ink dark:text-white">{item.name}</span>
                      <span className="block text-ink-3 dark:text-slate-400">
                        {t('store.orders.qty')}: {item.quantity} × {item.price} {item.currency || order.currency}
                      </span>
                    </div>
                  </div>
                  <span className="font-bold text-ink dark:text-white">
                    {(item.quantity * item.price).toFixed(2)} {item.currency || order.currency}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-line pt-3 text-sm font-bold dark:border-slate-800">
              <span className="text-ink dark:text-white">{t('store.orders.totalAmount')}</span>
              <span className="text-brand dark:text-brand-tint">
                {order.totalAmount.toFixed(2)} {order.currency}
              </span>
            </div>
          </div>

          {/* Payment & Verification Info */}
          <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <h4 className="mb-3 text-xs font-bold text-ink-2 dark:text-slate-300">
              {t('store.orders.paymentVerification')}
            </h4>
            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
              <div>
                <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.paymentMethod')}</span>
                <span className="font-semibold uppercase text-ink dark:text-white">
                  {order.paymentMethod}
                </span>
              </div>
              <div>
                <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.paymentStatus')}</span>
                <Badge variant={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'refunded' ? 'danger' : 'warning'}>
                  {order.paymentStatus}
                </Badge>
              </div>
              {order.codStatus && (
                <div>
                  <span className="block text-ink-3 dark:text-slate-400">{t('store.orders.codStatus')}</span>
                  <Badge variant={order.codStatus === 'confirmed' ? 'success' : order.codStatus === 'cancelled' ? 'danger' : 'warning'}>
                    {order.codStatus}
                  </Badge>
                </div>
              )}
            </div>

            {order.manualPaymentAttachmentUrl && (
              <div className="mt-3 rounded-lg border border-line p-3 dark:border-slate-800">
                <span className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400">
                  {t('store.orders.paymentReceipt')}
                </span>
                <a
                  href={order.manualPaymentAttachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:underline dark:text-brand-tint"
                >
                  📎 {t('store.orders.viewReceipt')}
                </a>
              </div>
            )}
          </div>

          {/* Status Changer Ladder */}
          <form
            onSubmit={handleUpdateStatus}
            className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850"
          >
            <h4 className="mb-3 text-xs font-bold text-ink-2 dark:text-slate-300">
              {t('store.orders.updateStatus')}
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-ink-3 dark:text-slate-400">
                  {t('store.orders.selectStatus')}
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as CatalogOrderStatus)}
                  className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                >
                  {CATALOG_ORDER_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {t(`store.status.${st}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-3 dark:text-slate-400">
                  {t('store.orders.statusNote')}
                </label>
                <input
                  type="text"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Optional transition note or tracking link"
                  className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notifyCustomer"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  className="rounded border-line text-brand focus:ring-brand"
                />
                <label
                  htmlFor="notifyCustomer"
                  className="text-xs font-medium text-ink dark:text-white cursor-pointer"
                >
                  {t('store.orders.notifyCustomerWhatsApp')}
                </label>
              </div>

              <div className="flex justify-end pt-1">
                <Button type="submit" size="sm" variant="primary" loading={updatingStatus}>
                  {t('store.orders.updateStatus')}
                </Button>
              </div>
            </div>
          </form>

          {/* Internal Staff Notes */}
          <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <h4 className="mb-3 text-xs font-bold text-ink-2 dark:text-slate-300">
              {t('store.orders.internalNotes')} ({(order.notes || []).length})
            </h4>

            <div className="mb-3 space-y-2 max-h-48 overflow-y-auto">
              {(order.notes || []).length === 0 ? (
                <p className="text-xs text-ink-3 dark:text-slate-400">{t('store.orders.noNotes')}</p>
              ) : (
                order.notes.map((n) => (
                  <div
                    key={n.id}
                    className="rounded-lg bg-surface-subtle p-2.5 text-xs dark:bg-slate-900"
                  >
                    <div className="flex items-center justify-between text-[10px] text-ink-3 dark:text-slate-400">
                      <span className="font-semibold text-ink-2 dark:text-slate-300">{n.author}</span>
                      <span>{new Date(n.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="mt-1 text-ink dark:text-white">{n.note}</p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddNote} className="flex gap-2">
              <input
                type="text"
                required
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={t('store.orders.addNotePlaceholder')}
                className="flex-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
              <Button type="submit" size="sm" variant="secondary" loading={addingNote}>
                {t('store.orders.addNote')}
              </Button>
            </form>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
